/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * useHeatmapCellDetail — drill-down data hook for the side drawer.
 *
 * Keyed on (precinct_num, period_start, bucket). When those change the hook
 * refetches. Pagination is handled via the `loadMore` callback, which
 * appends to the existing list without clearing.
 *
 * This hook is intentionally independent of `useEngagementHeatmap` so that
 * opening / paging the drawer does not invalidate the grid fetch.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchEngagementHeatmapDetail,
  type HeatmapDetailResponse,
  type HeatmapDetailRow,
} from '@/api/narrative';

export interface CellKey {
  precinct_num: string;
  period_start: string; // ISO date (YYYY-MM-DD)
}

export interface UseHeatmapCellDetailArgs {
  cell: CellKey | null;
  bucket: 'weekly' | 'daily' | 'monthly';
  pageSize?: number;
}

export interface UseHeatmapCellDetailResult {
  rows: HeatmapDetailRow[];
  totalKnocks: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  kAnonymityApplied: boolean;
  canvasserNamesRedacted: boolean;
  loadMore: () => void;
  refetch: () => void;
}

function mapError(e: any): string {
  const status = e?.response?.status as number | undefined;
  if (status === 403) return 'Access Denied — this drill-down is role-walled.';
  if (status === 401) return 'Not authenticated.';
  if (status === 404) return 'No data found for this cell.';
  if (status === 422) return 'Invalid drill-down parameters.';
  if (status) return `Backend error ${status}`;
  return 'Could not reach the backend.';
}

export function useHeatmapCellDetail(
  args: UseHeatmapCellDetailArgs
): UseHeatmapCellDetailResult {
  const { cell, bucket, pageSize = 25 } = args;
  const [rows, setRows] = useState<HeatmapDetailRow[]>([]);
  const [totalKnocks, setTotalKnocks] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [kAnonymityApplied, setKAnonymityApplied] = useState<boolean>(false);
  const [canvasserNamesRedacted, setCanvasserNamesRedacted] = useState<boolean>(false);
  const [nonce, setNonce] = useState<number>(0);

  // The cancellation flag must be a ref so the loadMore closure can
  // respect the latest cell change too.
  const cancelledRef = useRef<boolean>(false);

  // `bucket` is narrowed to the parent heatmap's supported range.
  const detailBucket: 'daily' | 'weekly' | 'monthly' =
    bucket === 'daily' ? 'daily' : bucket === 'monthly' ? 'monthly' : 'weekly';

  // Initial load / refetch on cell change.
  useEffect(() => {
    // No cell selected → clear state and bail.
    if (!cell) {
      setRows([]);
      setTotalKnocks(0);
      setHasMore(false);
      setLoading(false);
      setError(null);
      setKAnonymityApplied(false);
      setCanvasserNamesRedacted(false);
      return;
    }

    cancelledRef.current = false;
    setLoading(true);
    setError(null);

    fetchEngagementHeatmapDetail({
      precinct_num: cell.precinct_num,
      period_start: cell.period_start,
      bucket: detailBucket,
      limit: pageSize,
      offset: 0,
    })
      .then((d: HeatmapDetailResponse) => {
        if (cancelledRef.current) return;
        setRows(d.knocks);
        setTotalKnocks(d.total_knocks);
        setHasMore(d.has_more);
        setKAnonymityApplied(d.k_anonymity_applied);
        setCanvasserNamesRedacted(d.canvasser_names_redacted);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelledRef.current) return;
        setError(mapError(e));
        setLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [cell?.precinct_num, cell?.period_start, detailBucket, pageSize, nonce]);

  const loadMore = useCallback(() => {
    if (!cell || !hasMore || loadingMore) return;
    setLoadingMore(true);

    const nextOffset = rows.length;
    fetchEngagementHeatmapDetail({
      precinct_num: cell.precinct_num,
      period_start: cell.period_start,
      bucket: detailBucket,
      limit: pageSize,
      offset: nextOffset,
    })
      .then((d) => {
        if (cancelledRef.current) return;
        setRows((prev) => [...prev, ...d.knocks]);
        setHasMore(d.has_more);
        setLoadingMore(false);
      })
      .catch((e: any) => {
        if (cancelledRef.current) return;
        setError(mapError(e));
        setLoadingMore(false);
      });
  }, [cell, hasMore, loadingMore, rows.length, detailBucket, pageSize]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return {
    rows,
    totalKnocks,
    hasMore,
    loading,
    loadingMore,
    error,
    kAnonymityApplied,
    canvasserNamesRedacted,
    loadMore,
    refetch,
  };
}
